import React, { useState, useEffect, useRef } from 'react';
import ChatBot from './ChatBot';
import styled from 'styled-components';
import { createKnowledgeBase, getKnowledgeBaseByPagination } from '../../services/knowledgeBaseService';


const DraggablePanel = styled.div`
  position: fixed;
  top: ${props => props.top || 80}px;
  right: 0;
  width: 400px;
  height: 600px;
  max-height: 90vh;
  background: #fff;
  box-shadow: 0 2px 16px rgba(0,0,0,0.18);
  border-radius: 12px 0 0 12px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
  user-select: none;
`;

const DragHandle = styled.div`
  width: 100%;
  height: 32px;
  cursor: grab;
  background: #f3f5f7;
  border-radius: 12px 0 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #888;
  letter-spacing: 1px;
`;

const ChatBotContainer = ({ knowledgeBaseCode, conversationId, context, onClose }) => {
  const [isChatOpen] = useState(true); // Always open in docked mode
  const [actualKnowledgeBaseCode, setActualKnowledgeBaseCode] = useState(knowledgeBaseCode);
  const [isLoading, setIsLoading] = useState(false);
  const [panelTop, setPanelTop] = useState(80); // Initial top offset
  const dragOffset = useRef(0);

  // Handle KB code resolution
  useEffect(() => {
    const resolveKnowledgeBaseCode = async () => {
      setIsLoading(true);
      try {
        // Check if claim has value_stream
        if (context && context.knowledgebase) {
          setActualKnowledgeBaseCode(context.knowledgebase);
        } else {
          // Query the knowledgebase table and use the first record's code
          const filter = {};
          const sort = {created_at: "desc"};
          const query = {limit: 1}
          const kbResponse = await getKnowledgeBaseByPagination(filter, sort, query);
          
          if (kbResponse && kbResponse.length > 0) {
            // Use the first KB record
            setActualKnowledgeBaseCode(kbResponse[0].code);
            console.log("Using existing KB:", kbResponse[0].code);
          } else {
            // Create a placeholder KB if none exists
            const placeholderKB = {
              name: "placeholder",
              description: "Placeholder knowledge base created automatically",
              code: `KB-${new Date().getTime()}`,
              company_cd: context?.company_cd || "default"
            };
            
            const newKB = await createKnowledgeBase(placeholderKB);
            console.log("Created new placeholder KB:", newKB);
            setActualKnowledgeBaseCode(placeholderKB.code);
          }
        }
      } catch (error) {
        console.error("Error resolving knowledge base code:", error);
        // Fallback to a default KB code format with timestamp
        const fallbackCode = `KB-${new Date().getTime()}`;
        setActualKnowledgeBaseCode(fallbackCode);
      } finally {
        setIsLoading(false);
      }
    };

    resolveKnowledgeBaseCode();
  }, [knowledgeBaseCode, context]);


  // Drag logic
  const handleDragStart = (e) => {
    dragOffset.current = (e.touches ? e.touches[0].clientY : e.clientY) - panelTop;
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchmove', handleDragMove);
    document.addEventListener('touchend', handleDragEnd);
  };

  const handleDragMove = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let newTop = clientY - dragOffset.current;
    newTop = Math.max(0, Math.min(window.innerHeight - 100, newTop));
    setPanelTop(newTop);
  };

  const handleDragEnd = () => {
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchmove', handleDragMove);
    document.removeEventListener('touchend', handleDragEnd);
  };

  if (isLoading) return null;

  return (
    <DraggablePanel top={panelTop}>
      <DragHandle
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        title="Drag to move chatbot vertically"
      >
        MetaClaims AI
      </DragHandle>
      <div style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
        <ChatBot
          isOpen={isChatOpen}
          onClose={onClose}
          knowledgeBaseCode={actualKnowledgeBaseCode}
          conversationId={`AH-${conversationId}`}
          context={context}
        />
      </div>
    </DraggablePanel>
  );
};

export default ChatBotContainer;
